import React, { useState } from "react";
import axios from "axios";
import { brand } from "../mock";
import { Mail, Phone, ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = ({ hideHeader = false }) => {
  const [form, setForm] = useState({ name: "", org: "", email: "", phone: "", message: "", consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittedAction, setSubmittedAction] = useState("enquiry");

  const hasRequiredFields = Boolean(
    form.name.trim() && form.email.trim() && form.org.trim() && form.message.trim() && form.consent
  );

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submitLead = async (actionType) => {
    if (!hasRequiredFields) {
      toast({ title: "Missing details", description: "Please fill name, email, organization, message, and consent before continuing." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, {
        name: form.name,
        email: form.email,
        organization: form.org,
        phone: form.phone || null,
        message: form.message,
        source: actionType === "appointment" ? "appointment_request" : "contact_form",
        request_type: actionType,
      });
      setSubmittedAction(actionType);
      setSubmitted(true);
      toast({
        title: actionType === "appointment" ? "Details received" : "Thanks!",
        description: actionType === "appointment"
          ? "Your appointment request has been sent successfully. We will confirm the call over email."
          : "Your message has been received. We'll reach out shortly.",
      });
      if (actionType === "appointment") {
        window.open(brand.bookingUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Submission failed", description: typeof msg === "string" ? msg : "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await submitLead("enquiry");
  };

  const onBookAppointment = async () => {
    await submitLead("appointment");
  };

  return (
    <section id="contact-form" className={`bg-[#0B1B3B] text-white relative overflow-hidden ${hideHeader ? "pt-4 pb-20 -mt-2" : "py-24"}`}>
      <div className="absolute inset-0 grid-lines-bg opacity-40" />
      <div className="absolute -bottom-24 -left-24 w-[500px] h-[500px] rounded-full orb"
           style={{ background: "radial-gradient(closest-side, rgba(30,86,227,0.25), transparent 70%)" }} />

      <div className="container-x relative grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 reveal">
          {!hideHeader && (
            <>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#7CA6FF] bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
                Let’s Talk
              </span>
              <h2 className="mt-4 text-3xl md:text-[42px] font-bold leading-tight">Put your data on the pedestal</h2>
              <p className="mt-5 text-slate-300 leading-relaxed">
                Take control of your data and build reliable systems that empower your data teams and drive better business outcomes.
              </p>
            </>
          )}

          <div className={`${hideHeader ? "mt-2" : "mt-10"} space-y-4`}>
            <div className="flex items-start gap-3">
              <Mail className="text-[#7CA6FF] mt-1" size={18} />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Email</div>
                <a href={`mailto:${brand.email}`} className="text-[15px] font-medium hover:text-[#7CA6FF]">{brand.email}</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="text-[#7CA6FF] mt-1" size={18} />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Phone</div>
                <a href="tel:+18566060178" className="text-[15px] font-medium hover:text-[#7CA6FF]" data-testid="contact-phone-display">+1 (856) 606-0178</a>
              </div>
            </div>
            <div className="rounded-[28px] border border-[#7CA6FF]/30 bg-gradient-to-br from-[#1E56E3]/30 via-[#12327D]/40 to-white/5 px-5 py-6 shadow-[0_20px_60px_-30px_rgba(30,86,227,0.9)] ring-1 ring-[#7CA6FF]/20">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#A9C1FF]">
                <Sparkles size={12} /> Or start here
              </div>
              <h3 className="mt-4 text-2xl font-extrabold leading-tight text-white">Fill the form to send an enquiry or book an appointment</h3>
              <p className="mt-3 text-sm text-slate-200 leading-relaxed">
                Both enquiries and appointment requests are sent directly to our Gmail inbox, so we can reply quickly and confirm the next step with you over email.
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 reveal">
          <div className="rounded-[28px] border border-white/20 bg-gradient-to-br from-[#FFFFFF] to-[#F7FAFF] dark:from-[#102248] dark:to-[#0B1B3B] text-[#0B1B3B] dark:text-white p-8 md:p-10 shadow-[0_30px_90px_-40px_rgba(11,27,59,0.7)] ring-1 ring-white/30">
            {submitted ? (
              <div className="flex flex-col items-center text-center py-10">
                <CheckCircle2 className="text-emerald-500" size={56} />
                <h3 className="mt-4 text-2xl font-bold">Thank you!</h3>
                <p className="mt-2 text-slate-600 max-w-md">
                  {submittedAction === "appointment"
                    ? "Your appointment request has been sent successfully. We’ll confirm the next available slot over email."
                    : "Your message has been received. We’ll reach out shortly to discuss your data engineering needs."}
                </p>
                <button onClick={() => setSubmitted(false)} className="btn-dark mt-6" data-testid="contact-reset-button">Send another message</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form-panel">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[#1E56E3] dark:text-[#A9C1FF]">Contact form</div>
                  <h3 className="mt-2 text-2xl font-bold text-[#0B1B3B] dark:text-white">Share your requirement first</h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    Once you complete this form, you can either send an enquiry directly or send an appointment request to our Gmail inbox.
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#1E56E3]/15 bg-[#EEF3FF] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1E56E3]">
                    Required fields unlock the action buttons
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Name*</label>
                    <input name="name" value={form.name} onChange={onChange} required placeholder="Your full name" data-testid="contact-name-input" className="mt-1 w-full rounded-2xl border border-slate-300 bg-[#FFFFFF] dark:bg-[#FFFFFF] px-4 py-3.5 text-slate-950 dark:text-slate-950 shadow-sm outline-none focus:border-[#1E56E3] focus:ring-2 focus:ring-[#1E56E3]/15 transition-all placeholder:text-slate-500 dark:placeholder:text-slate-500" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Organization*</label>
                    <input name="org" value={form.org} onChange={onChange} required placeholder="Company or business name" data-testid="contact-organization-input" className="mt-1 w-full rounded-2xl border border-slate-300 bg-[#FFFFFF] dark:bg-[#FFFFFF] px-4 py-3.5 text-slate-950 dark:text-slate-950 shadow-sm outline-none focus:border-[#1E56E3] focus:ring-2 focus:ring-[#1E56E3]/15 transition-all placeholder:text-slate-500 dark:placeholder:text-slate-500" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Email*</label>
                    <input name="email" type="email" value={form.email} onChange={onChange} required placeholder="name@company.com" data-testid="contact-email-input" className="mt-1 w-full rounded-2xl border border-slate-300 bg-[#FFFFFF] dark:bg-[#FFFFFF] px-4 py-3.5 text-slate-950 dark:text-slate-950 shadow-sm outline-none focus:border-[#1E56E3] focus:ring-2 focus:ring-[#1E56E3]/15 transition-all placeholder:text-slate-500 dark:placeholder:text-slate-500" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Phone</label>
                    <input name="phone" value={form.phone} onChange={onChange} placeholder="Optional phone number" data-testid="contact-phone-input" className="mt-1 w-full rounded-2xl border border-slate-300 bg-[#FFFFFF] dark:bg-[#FFFFFF] px-4 py-3.5 text-slate-950 dark:text-slate-950 shadow-sm outline-none focus:border-[#1E56E3] focus:ring-2 focus:ring-[#1E56E3]/15 transition-all placeholder:text-slate-500 dark:placeholder:text-slate-500" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Message*</label>
                  <textarea name="message" value={form.message} onChange={onChange} rows={5} required placeholder="Tell us about your data platform, migration, reporting, or AI requirement" data-testid="contact-message-input" className="mt-1 w-full rounded-2xl border border-slate-300 bg-[#FFFFFF] dark:bg-[#FFFFFF] px-4 py-3.5 text-base leading-relaxed text-slate-950 dark:text-slate-950 shadow-sm outline-none focus:border-[#1E56E3] focus:ring-2 focus:ring-[#1E56E3]/15 transition-all resize-none placeholder:text-slate-500 dark:placeholder:text-slate-500" />
                </div>
                <label className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300 pt-2">
                  <input type="checkbox" name="consent" checked={form.consent} onChange={onChange} data-testid="contact-consent-checkbox" className="mt-1 h-4 w-4 accent-[#1E56E3]" />
                  <span>I agree to be contacted by V4 Data And AI Solutions through the information provided.</span>
                </label>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button type="submit" disabled={loading || !hasRequiredFields} className="btn-primary disabled:cursor-not-allowed disabled:opacity-50" data-testid="contact-send-enquiry-button">
                    {loading ? (<><Loader2 size={16} className="animate-spin" /> Submitting…</>) : (<>Send Enquiry <ArrowRight size={16} /></>)}
                  </button>
                  <button
                    type="button"
                    onClick={onBookAppointment}
                    disabled={loading || !hasRequiredFields}
                    className="btn-dark disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="contact-book-appointment-button"
                  >
                    {loading ? (<><Loader2 size={16} className="animate-spin" /> Preparing…</>) : (<>Book Appointment <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
